import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';

/**
 * The websocket half of chat (P18b): the names the two sides agree on, and the
 * one payload that crosses.
 *
 * ## Why any of this is in `contracts` rather than in the gateway
 *
 * A socket event name is a wire contract exactly as much as a route is. Left as
 * string literals it would be written twice — once in the gateway, once in the
 * mobile client — and the two would drift the first time one of them was
 * renamed, silently, because nothing typechecks a string against a string in
 * another app (U3).
 */

/** Events the **client** sends. Both are subscription management, never writes. */
export const CHAT_SOCKET_CLIENT_EVENTS = {
  /**
   * Ask to receive signals for one conversation.
   *
   * Acknowledged, not fire-and-forget: a client that cannot tell a successful
   * join from a refused one has no way to know whether its screen is live or
   * silently dead, and would draw a working chat that never updates.
   */
  joinConversation: 'chat:join',
  /** Stop receiving signals for one conversation — the screen was closed. */
  leaveConversation: 'chat:leave',
} as const;

/** Events the **server** sends. */
export const CHAT_SOCKET_SERVER_EVENTS = {
  /**
   * Something was said in a conversation this socket has joined.
   *
   * **A hint, never the message.** See `ChatMessageSignalSchema`.
   */
  messageCreated: 'chat:message-created',
} as const;

export type ChatSocketClientEvent =
  (typeof CHAT_SOCKET_CLIENT_EVENTS)[keyof typeof CHAT_SOCKET_CLIENT_EVENTS];
export type ChatSocketServerEvent =
  (typeof CHAT_SOCKET_SERVER_EVENTS)[keyof typeof CHAT_SOCKET_SERVER_EVENTS];

/**
 * The fan-out payload — **ids and a timestamp, never a body**.
 *
 * This is the single most load-bearing decision in the phase, so it is stated
 * where both sides can read it rather than in one implementation's comments.
 *
 * A message is withheld from a reader for reasons the *reader* determines:
 * moderation hides it (P20), a block closes the conversation (P19), a page is
 * filtered per viewer by `MessageRepository.pageBefore`. A broadcast that
 * carried the text would have to re-derive every one of those rules a second
 * time, in a second place, for a second audience — and the first rule anyone
 * forgot would deliver, over a socket, something the REST surface refuses to
 * return.
 *
 * So the socket says only *that* something changed, and the client re-reads
 * through `GET /conversations/:id/messages` — the same filtered path, the same
 * authorization, one implementation. The cost is one extra round trip on a
 * screen that is already open; the benefit is that the socket **cannot** leak,
 * because it never holds anything to leak.
 *
 * `senderId` is included for one reason: a client that sent the message already
 * rendered it optimistically and must be able to ignore its own echo without a
 * refetch.
 */
export const ChatMessageSignalSchema = z
  .object({
    conversationId: UuidSchema,
    messageId: UuidSchema,
    senderId: UuidSchema,
    // Bare `z.iso.datetime()`, matching `MessageSchema.sentAt` — the two describe
    // the same column and a stricter shape here would refuse what REST returns.
    sentAt: z.iso.datetime(),
  })
  .strict();

export type ChatMessageSignal = z.infer<typeof ChatMessageSignalSchema>;

/**
 * The reply to `chat:join`.
 *
 * A refusal is `{ joined: false }` with no reason attached, and that is
 * deliberate rather than lazy: the HTTP surface answers a conversation the
 * caller is not in and a conversation that does not exist with the *same*
 * `conversationNotFound` code (N13 — telling them apart is a match oracle for
 * any pair of ids somebody can guess). A socket that distinguished them would
 * reopen through the websocket exactly the hole the REST codes were collapsed
 * to close.
 *
 * `conversationId` is a **plain string, not `UuidSchema`** — it is the client's
 * own request echoed back so it can correlate the ack with the join it sent,
 * and a client that asked about a malformed id must still be answered. Typing
 * it as a uuid made the schema unable to parse the very refusal the gateway
 * sends for a malformed id, which is a contract that refuses its own output.
 * It is bounded, because it is echoed input.
 */
export const ChatJoinResultSchema = z
  .object({
    conversationId: z.string().max(128),
    joined: z.boolean(),
  })
  .strict();

export type ChatJoinResult = z.infer<typeof ChatJoinResultSchema>;

/**
 * The room name for a conversation.
 *
 * Namespaced rather than the bare id, because socket.io rooms and socket ids
 * share one namespace: every socket is implicitly in a room named after its own
 * id, so an unprefixed room would collide with a socket id the day the two id
 * shapes ever coincide. One function, used by the gateway to join and to
 * broadcast, so the two can never disagree about the name.
 */
export const chatConversationRoom = (conversationId: string): string =>
  `conversation:${conversationId}`;

/**
 * Where the client puts its access token in the socket.io handshake.
 *
 * `auth`, not a query parameter: query strings land in proxy and server access
 * logs, and this is a bearer credential (the same reasoning that keeps it out of
 * a URL over HTTP).
 */
export const CHAT_SOCKET_AUTH_TOKEN_KEY = 'token';

/** The socket.io namespace the gateway serves. */
export const CHAT_SOCKET_NAMESPACE = '/chat';
